import React, { useEffect, useState, useMemo } from 'react';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Button, Switch, Typography, Box, Chip, Checkbox, TableSortLabel
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  Send, RestartAlt, EventNote, Layers,
  GppBad, GppGood
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../../../api/axios';

const DayjsAdapter = AdapterDayjs as any;

interface EnumOption {
  dbName: string;
  label: string;
  currentlyDisabled: boolean;
}

interface Item {
  apiId: number;
  marketHashName: string;
  itemType: string;
  groupName: string;
  currentlyDisabledItem: boolean;
}

type Order = 'asc' | 'desc';

const ItemDisable = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [types, setTypes] = useState<EnumOption[]>([]);
  const [groups, setGroups] = useState<EnumOption[]>([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [bulkUntil, setBulkUntil] = useState<Dayjs | null>(null);

  const [itemChanges, setItemChanges] = useState<Map<number, { disabled: boolean, until: Dayjs | null }>>(new Map());
  const [loading, setLoading] = useState(false);

  // Sorting state - default to desc to show locked items first if backend supports it
  const [order, setOrder] = useState<Order>('desc');

  const fetchData = async () => {
    try {
      // Added sort parameter to API call for server-side sorting
      const [itemRes, typeRes, groupRes] = await Promise.all([
        api.get(`/system/item?page=${page}&size=${rowsPerPage}&sort=currentlyDisabledItem,${order}`),
        api.get('/system/item/types'),
        api.get('/system/item/groups')
      ]);
      setItems(itemRes.data.content);
      setTotalCount(itemRes.data.totalElements);
      setTypes(typeRes.data);
      setGroups(groupRes.data);
      setItemChanges(new Map());
    } catch (err) {
      toast.error('Error fetching data');
    }
  };

  // Re-fetch when page, size OR order changes
  useEffect(() => { fetchData(); }, [page, rowsPerPage, order]);

  const toggleType = (dbName: string) => {
    setSelectedTypes(prev => prev.includes(dbName) ? prev.filter(t => t !== dbName) : [...prev, dbName]);
  };

  const toggleGroup = (dbName: string) => {
    setSelectedGroups(prev => prev.includes(dbName) ? prev.filter(g => g !== dbName) : [...prev, dbName]);
  };

  const handleBulkAction = async (mode: 'type' | 'group', active: boolean) => {
    const selection = mode === 'type' ? selectedTypes : selectedGroups;
    if (selection.length === 0) return toast.warn('Select at least one entry');

    setLoading(true);
    const payload = selection.map(val => ({
      [mode === 'type' ? 'itemType' : 'itemGroup']: val,
      disabled: active,
      disabledUntil: bulkUntil?.toISOString() || null
    }));

    try {
      await api.post(`/system/item/${mode}`, payload);

      if (mode === 'type') {
        setTypes(prev => prev.map(t => selection.includes(t.dbName) ? { ...t, currentlyDisabled: active } : t));
        setSelectedTypes([]);
      } else {
        setGroups(prev => prev.map(g => selection.includes(g.dbName) ? { ...g, currentlyDisabled: active } : g));
        setSelectedGroups([]);
      }

      toast.success(`Bulk updated ${selection.length} ${mode}s`);
      fetchData();
    } catch (err) {
      toast.error('Bulk update failed');
    } finally {
      setLoading(false);
    }
  };

  const saveItemChanges = async () => {
    if (itemChanges.size === 0) return;
    const payload = Array.from(itemChanges.entries()).map(([id, data]) => ({
      itemApiId: id,
      disabled: data.disabled,
      disabledUntil: data.until?.toISOString() || null
    }));

    try {
      await api.post('/system/item/', payload);
      toast.success(`${payload.length} items updated`);
      fetchData();
    } catch (err) {
      toast.error('Failed to save changes');
    }
  };

  // Memoize effective state but remove manual sorting since it's now server-side
  const processedItems = useMemo(() => {
    return items.map(item => {
      const draft = itemChanges.get(item.apiId);
      const typeLock = types.find(t => t.dbName === item.itemType)?.currentlyDisabled;
      const groupLock = groups.find(g => g.dbName === item.groupName)?.currentlyDisabled;
      const isInherited = typeLock || groupLock;
      const isCurrentlyDisabled = draft !== undefined ? draft.disabled : (item.currentlyDisabledItem || isInherited);

      return { ...item, isCurrentlyDisabled, isInherited, isDrafted: draft !== undefined };
    });
  }, [items, itemChanges, types, groups]);

  return (
      <LocalizationProvider dateAdapter={DayjsAdapter}>
        <div className="space-y-6">

          {/* Bulk Management Panel */}
          <Paper className="p-6 bg-dark-primary border border-neutral-800 rounded-2xl shadow-xl">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg text-blue-500">
                  <Layers />
                </div>
                <div>
                  <Typography className="text-white font-bold text-lg">Bulk Management & Rules</Typography>
                  <Typography className="text-slate-400 text-xs font-medium italic">Define global visibility restrictions</Typography>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                <MobileDateTimePicker
                    label="Restriction Expiry"
                    value={bulkUntil}
                    onChange={(val) => setBulkUntil(val)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: 230,
                          '& .MuiInputBase-root': { bgcolor: '#0a0a0a', color: 'white' },
                          '& .MuiInputLabel-root': { color: '#94a3b8' }
                        }
                      }
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={<Send />}
                    onClick={saveItemChanges}
                    disabled={itemChanges.size === 0 || loading}
                    className={`h-10 px-6 rounded-xl font-bold transition-all text-white ${itemChanges.size > 0 ? 'bg-blue-600 hover:bg-blue-500' : 'bg-neutral-800 text-neutral-500'}`}
                >
                  Sync ({itemChanges.size})
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              {/* Types Selection Grid */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Typography className="text-neutral-400 text-[10px] font-black uppercase tracking-widest">Item Types</Typography>
                  <div className="flex gap-2">
                    <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={() => handleBulkAction('type', true)}
                        className={`text-[10px] font-bold px-4 py-1.5 rounded-lg !text-white transition-all disabled:bg-neutral-800 disabled:text-neutral-500`}
                        disabled={selectedTypes.length === 0}
                    >
                      Lock
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleBulkAction('type', false)}
                        className={`text-[10px] font-bold px-4 py-1.5 rounded-lg !text-white transition-all disabled:bg-neutral-800 disabled:text-neutral-500`}
                        disabled={selectedTypes.length === 0}
                    >
                      Unlock
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-60 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-700">
                  {types.map(t => (
                      <div
                          key={t.dbName}
                          onClick={() => toggleType(t.dbName)}
                          className={`flex items-center justify-between p-2 rounded-lg border transition-all cursor-pointer ${selectedTypes.includes(t.dbName) ? 'bg-blue-600/20 border-blue-500' : 'bg-neutral-800 border-neutral-800 hover:border-neutral-600'}`}
                      >
                        <div className="flex items-center gap-2 overflow-hidden">
                          <Checkbox size="small" checked={selectedTypes.includes(t.dbName)} className="p-0 text-neutral-600" />
                          <span className="text-[11px] text-neutral-200 truncate font-medium">{t.label}</span>
                        </div>
                        {t.currentlyDisabled && <GppBad className="text-red-500" sx={{ fontSize: 14 }} />}
                      </div>
                  ))}
                </div>
              </div>

              {/* Groups Selection Grid */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Typography className="text-neutral-400 text-[10px] font-black uppercase tracking-widest">Item Groups</Typography>
                  <div className="flex gap-2">
                    <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={() => handleBulkAction('group', true)}
                        className={`text-[10px] font-bold px-4 py-1.5 rounded-lg !text-white transition-all disabled:bg-neutral-800 disabled:text-neutral-500`}
                        disabled={selectedGroups.length === 0}
                    >
                      Lock
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleBulkAction('group', false)}
                        className={`text-[10px] font-bold px-4 py-1.5 rounded-lg !text-white transition-all disabled:bg-neutral-800 disabled:text-neutral-500`}
                        disabled={selectedGroups.length === 0}
                    >
                      Unlock
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-60 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-700">
                  {groups.map(g => (
                      <div
                          key={g.dbName}
                          onClick={() => toggleGroup(g.dbName)}
                          className={`flex items-center justify-between p-2 rounded-lg border transition-all cursor-pointer ${selectedGroups.includes(g.dbName) ? 'bg-blue-600/20 border-blue-500' : 'bg-neutral-800 border-neutral-800 hover:border-neutral-600'}`}
                      >
                        <div className="flex items-center gap-2 overflow-hidden">
                          <Checkbox size="small" checked={selectedGroups.includes(g.dbName)} className="p-0 text-neutral-600" />
                          <span className="text-[11px] text-neutral-200 truncate font-medium">{g.label}</span>
                        </div>
                        {g.currentlyDisabled && <GppBad className="text-red-500" sx={{ fontSize: 14 }} />}
                      </div>
                  ))}
                </div>
              </div>
            </div>
          </Paper>

          {/* Individual Items Table */}
          <TableContainer component={Paper} className="rounded-2xl border border-neutral-800 overflow-hidden shadow-2xl bg-dark-primary">
            <Table size="small">
              <TableHead className="bg-neutral-800">
                <TableRow>
                  <TableCell className="font-bold text-[10px] uppercase tracking-widest text-neutral-400 py-4 border-b border-neutral-800">Item Details</TableCell>
                  <TableCell className="font-bold text-[10px] uppercase tracking-widest text-neutral-400 text-center border-b border-neutral-800">Category</TableCell>
                  <TableCell className="font-bold text-[10px] uppercase tracking-widest text-neutral-400 border-b border-neutral-800">
                    <TableSortLabel
                        active={true}
                        direction={order}
                        onClick={() => setOrder(order === 'asc' ? 'desc' : 'asc')}
                        sx={{
                          '&.MuiTableSortLabel-root': { color: '#a3a3a3' },
                          '&.MuiTableSortLabel-root:hover': { color: 'white' },
                          '&.Mui-active': { color: 'white' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' }
                        }}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className="font-bold text-[10px] uppercase tracking-widest text-neutral-400 text-right border-b border-neutral-800">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processedItems.map((item) => {
                  return (
                      <TableRow key={item.apiId} className={`${item.isDrafted ? 'bg-neutral-900/10' : ''} border-b border-neutral-800 hover:bg-neutral-800/20`}>
                        <TableCell className="py-3 border-none">
                          <div className="flex flex-col">
                            <span className="font-bold text-sm text-neutral-100">{item.marketHashName}</span>
                            <span className="text-[10px] font-mono text-neutral-500 uppercase tracking-tighter">API ID: {item.apiId}</span>
                          </div>
                        </TableCell>
                        <TableCell align="center" className="border-none">
                          <div className="flex flex-col gap-1 items-center justify-center min-h-[40px]">
                            {item.itemType && (
                                <Chip label={item.itemType} sx={{ height: 16, fontSize: 8, bgcolor: '#262626', color: '#3b82f6', fontWeight: 900, border: '1px solid #262626' }} />
                            )}
                            {item.groupName && (
                                <Chip label={item.groupName} sx={{ height: 16, fontSize: 8, bgcolor: '#262626', color: '#a855f7', fontWeight: 900, border: '1px solid #262626' }} />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="border-none">
                          <div className="flex flex-col gap-1">
                            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black ${item.isCurrentlyDisabled ? 'bg-red-500/10 text-red-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                              {item.isCurrentlyDisabled ? <GppBad style={{fontSize: 10}} /> : <GppGood style={{fontSize: 10}} />}
                              {item.isCurrentlyDisabled ? 'LOCKED' : 'ACTIVE'}
                            </div>
                            {item.isInherited && !item.currentlyDisabledItem && (
                                <span className="text-[8px] text-red-400/60 font-black uppercase tracking-tighter">Policy Inherited</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell align="right" className="border-none">
                          <div className="flex justify-end items-center gap-2">
                            {item.isDrafted && (
                                <RestartAlt
                                    className="text-neutral-500 cursor-pointer hover:text-white"
                                    fontSize="small"
                                    onClick={() => {
                                      const next = new Map(itemChanges);
                                      next.delete(item.apiId);
                                      setItemChanges(next);
                                    }}
                                />
                            )}
                            <Switch
                                checked={item.isCurrentlyDisabled}
                                onChange={(e) => {
                                  const newChanges = new Map(itemChanges);
                                  newChanges.set(item.apiId, { disabled: e.target.checked, until: bulkUntil });
                                  setItemChanges(newChanges);
                                }}
                                color="error"
                                size="small"
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, n) => setPage(n)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                sx={{ color: '#737373', borderTop: '1px solid #262626' }}
            />
          </TableContainer>
        </div>
      </LocalizationProvider>
  );
};

export default ItemDisable;